(function () {
  const ROOT_SELECTOR = '[data-fb-product-finder]';
  const SELECTORS = {
    config: '[data-fb-finder-config]',
    stepsWrap: '.fb-finder__steps',
    actions: '.fb-finder__actions',
    step: '[data-fb-step]',
    answer: '[data-fb-answer]',
    back: '[data-fb-back]',
    restart: '[data-fb-restart]',
    progress: '[data-fb-progress]',
    progressLabel: '[data-fb-progress-label]',
    result: '[data-fb-result]',
    resultImage: '[data-fb-result-image]',
    resultTitle: '[data-fb-result-title]',
    resultText: '[data-fb-result-text]',
    resultReasons: '[data-fb-result-reasons]',
    resultPrice: '[data-fb-result-price]',
    resultBadges: '[data-fb-result-badges]',
    resultLink: '[data-fb-result-link]',
    addToCart: '[data-fb-add-to-cart]',
    status: '[data-fb-status]',
    alternatives: '[data-fb-alternatives]',
    sticky: '[data-fb-sticky]',
    stickyTitle: '[data-fb-sticky-title]',
    stickyAdd: '[data-fb-sticky-add]',
  };

  const EVENT_PREFIX = 'finder_';
  const RESULT_STEP = 'result';
  const PREFILL_SOURCE_PARAM = 'fb_source';
  const LEGACY_PRIORITY_PARAM = 'fb_priority';
  const LEGACY_FORMAT_BY_SOURCE_PRIORITY = {
    'homepage_quickfinder_all|variety': 'open',
    'homepage_quickfinder_gels|variety': 'gel',
    'homepage_quickfinder_no_caffeine|variety': 'open',
    'homepage_quickfinder_race|high_energy': 'gel',
    'homepage_quickfinder_long_ride|format_mix': 'mix',
    'homepage_quickfinder_chews|format_mix': 'solid',
  };
  const PREFILL_PARAMS = [
    { query: 'fb_use_case', question: 'use_case' },
    { query: 'fb_duration', question: 'duration' },
    { query: 'fb_caffeine', question: 'caffeine' },
    { query: 'fb_format', question: 'format' },
  ];
  const ALTERNATIVE_ROLE_ORDER = {
    drink_explorer: ['drink_long'],
    drink_long: ['drink_explorer'],
    bestseller_explorer: ['high_carb_race', 'race'],
    no_caffeine_starter: ['mix_starter'],
    high_carb_race: ['bestseller_explorer', 'race'],
    race: ['high_carb_race', 'bestseller_explorer', 'chew_explorer'],
    chew_explorer: ['race'],
    mix_starter: ['mix_drink_gel', 'mix_drink_gel_bar'],
    mix_drink_gel: ['mix_starter', 'mix_drink_gel_bar'],
    mix_drink_gel_bar: ['mix_drink_gel', 'mix_complete'],
    mix_complete: ['mix_drink_gel_bar', 'long'],
    long: ['mix_drink_gel_bar', 'mix_complete'],
  };
  // When no available box matches the chosen format, relax only the format (never the
  // caffeine rule) in this order, so a sold-out box never ends the finder empty.
  const FORMAT_FALLBACK_ORDER = {
    solid: ['mix', 'gel', 'open'],
    mix: ['gel', 'drink', 'open'],
    gel: ['mix', 'open'],
    drink: ['mix', 'open'],
    open: [],
  };
  // Mix boxes that actually contain bars or chews, preferred when a solid box is sold out.
  const SOLID_FALLBACK_ROLES = ['long', 'mix_complete', 'mix_drink_gel_bar', 'mix_starter'];
  const FALLBACK_FORMAT_LABELS = {
    drink: 'Sportgetränken',
    gel: 'Gels',
    solid: 'Riegeln & Chews',
    mix: 'mehreren Formaten',
  };
  const MATCH_REASON_LABELS = {
    use_case: {
      training: 'Training',
      long: 'Lange Einheit',
      race: 'Wettkampf-Test',
      test: 'Einfach testen',
    },
    duration: {
      under_90: 'Bis 90 Minuten',
      '90_180': '90 bis 180 Minuten',
      '180_plus': '3 Stunden plus',
      mixed: 'Variable Dauer',
    },
    caffeine: {
      no_caffeine: 'Ohne Koffein',
    },
    format: {
      drink: 'Sportgetränke & Carb-Pulver',
      gel: 'Gels',
      solid: 'Riegel & Chews',
      mix: 'Mix aus mehreren Formaten',
    },
  };

  function toList(value) {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    if (value == null) return [];
    return String(value)
      .split(/[,;|]/)
      .map((item) => item.trim())
      .filter((item) => item !== 'none')
      .filter(Boolean);
  }

  function parseConfig(root) {
    const script = root.querySelector(SELECTORS.config);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[FuelBox Finder] Invalid config JSON', error);
      return null;
    }
  }

  function packageHandle(pkg) {
    return String(pkg && pkg.handle ? pkg.handle : '');
  }

  function isPurchasable(pkg) {
    return Boolean(
      pkg &&
        pkg.stage === 'live' &&
        pkg.finderActive === true &&
        pkg.productExists === true &&
        pkg.available === true &&
        pkg.variantId &&
        (pkg.requiresBundleContents !== true || pkg.bundleReady === true)
    );
  }

  function isAutomaticCandidate(pkg) {
    return !String(pkg?.role || '').startsWith('brand_');
  }

  function violatesHardRules(pkg, answers) {
    if (!pkg) return true;

    const hardExclusions = toList(pkg.hardExclusions);
    const answerValues = Object.values(answers || {}).filter(Boolean);

    if (answerValues.some((answer) => hardExclusions.includes(answer))) return true;

    if (
      answers.caffeine === 'no_caffeine' &&
      (pkg.caffeinePolicy !== 'no_caffeine' || hardExclusions.includes('no_caffeine'))
    ) {
      return true;
    }

    if (answers.format && !matchesFormat(pkg, answers.format)) {
      return true;
    }

    return false;
  }

  function scorePackage(pkg, answers) {
    let score = 0;
    const priorityTieBreak = Number(pkg.priority || 0) / 100;
    const useCases = toList(pkg.useCaseFit);
    const durations = toList(pkg.durationFit);
    const role = String(pkg.role || '');
    const preferredRoles = preferredRolesForAnswers(answers);
    const roleIndex = preferredRoles.indexOf(role);

    if (roleIndex !== -1) score += Math.max(40, 160 - roleIndex * 40);
    if (answers.use_case && useCases.includes(answers.use_case)) score += 20;
    if (answers.duration && durations.includes(answers.duration)) {
      score += 12;
    } else if (answers.duration && answers.duration !== 'mixed' && durations.includes('mixed')) {
      score += 4;
    }
    if (answers.format && answers.format !== 'open' && matchesFormat(pkg, answers.format)) {
      score += 10;
    }
    if (answers.caffeine === 'no_caffeine' && pkg.caffeinePolicy === 'no_caffeine') score += 8;

    return score + priorityTieBreak;
  }

  function preferredRolesForAnswers(answers) {
    const useCase = String(answers?.use_case || '');
    const duration = String(answers?.duration || '');
    const caffeine = String(answers?.caffeine || '');
    const format = String(answers?.format || 'open');

    if (format === 'drink') {
      return useCase === 'race' || useCase === 'long' || duration === '180_plus'
        ? ['drink_long', 'drink_explorer']
        : ['drink_explorer', 'drink_long'];
    }

    if (format === 'gel') {
      if (caffeine === 'no_caffeine') return ['no_caffeine_starter'];
      if (useCase === 'race') return ['race', 'high_carb_race', 'bestseller_explorer'];
      if (useCase === 'long' || duration === '180_plus') {
        return ['high_carb_race', 'bestseller_explorer', 'race'];
      }
      return ['bestseller_explorer', 'high_carb_race', 'race'];
    }

    if (format === 'solid') {
      return useCase === 'race' ? ['race', 'chew_explorer'] : ['chew_explorer', 'race'];
    }

    if (format === 'mix') {
      if (caffeine === 'no_caffeine') return ['mix_starter'];
      if (useCase === 'race') {
        return duration === 'under_90'
          ? ['mix_drink_gel', 'mix_drink_gel_bar', 'mix_complete']
          : ['mix_complete', 'mix_drink_gel_bar', 'long'];
      }
      if (useCase === 'long') return ['long', 'mix_complete', 'mix_drink_gel_bar'];
      if (duration === '180_plus') return ['mix_complete', 'mix_drink_gel_bar', 'long'];
      if (duration === '90_180') return ['mix_drink_gel_bar', 'mix_complete', 'mix_drink_gel'];
      return ['mix_drink_gel', 'mix_starter', 'mix_drink_gel_bar'];
    }

    if (caffeine === 'no_caffeine') return ['mix_starter', 'no_caffeine_starter'];
    if (useCase === 'race') return ['race', 'mix_complete', 'high_carb_race', 'drink_long'];
    if (useCase === 'long') return ['long', 'mix_complete', 'high_carb_race', 'drink_long'];
    if (duration === '180_plus') return ['mix_complete', 'long', 'high_carb_race', 'drink_long'];
    if (duration === '90_180') {
      return ['mix_drink_gel_bar', 'bestseller_explorer', 'mix_complete', 'drink_explorer'];
    }
    return ['mix_starter', 'bestseller_explorer', 'drink_explorer', 'mix_drink_gel'];
  }

  function eligiblePackageList(packages, answers) {
    return (packages || []).filter(
      (pkg) => isPurchasable(pkg) && isAutomaticCandidate(pkg) && !violatesHardRules(pkg, answers)
    );
  }

  function rankPackages(packages, answers) {
    return eligiblePackageList(packages, answers).sort(
      (a, b) => scorePackage(b, answers) - scorePackage(a, answers)
    );
  }

  function choosePackageForAnswers(packages, answers) {
    const strict = rankPackages(packages, answers);
    if (strict.length) return { pkg: strict[0], resultType: 'standard', rankAnswers: answers };

    const format = String(answers?.format || 'open');
    const fallbackFormats = FORMAT_FALLBACK_ORDER[format] || ['open'];

    for (const fallbackFormat of fallbackFormats) {
      const rankAnswers = { ...answers, format: fallbackFormat };
      const ranked = rankPackages(packages, rankAnswers);
      if (!ranked.length) continue;

      if (format === 'solid' && fallbackFormat === 'mix') {
        const withSolids = ranked.filter((pkg) => SOLID_FALLBACK_ROLES.includes(String(pkg.role || '')));
        if (!withSolids.length) continue;
        const roleOrder = answers.use_case === 'race' ? ['mix_complete', 'long', 'mix_drink_gel_bar', 'mix_starter'] : SOLID_FALLBACK_ROLES;
        withSolids.sort((a, b) => roleOrder.indexOf(String(a.role)) - roleOrder.indexOf(String(b.role)));
        return { pkg: withSolids[0], resultType: 'fallback', rankAnswers };
      }

      return { pkg: ranked[0], resultType: 'fallback', rankAnswers };
    }

    return { pkg: null, resultType: 'empty', rankAnswers: answers };
  }

  function buildFallbackNote(answers) {
    const formatLabel = FALLBACK_FORMAT_LABELS[answers?.format];
    const caffeineSuffix = answers?.caffeine === 'no_caffeine' ? ' ohne Koffein' : '';
    const subject = formatLabel ? `Eine Box mit ${formatLabel}${caffeineSuffix}` : 'Die exakt passende Box';

    return `${subject} ist gerade nicht verfügbar. Diese Box kommt deinen Antworten am nächsten.`;
  }

  function rankAlternatives(packages, selectedPackage, answers) {
    const selectedHandle = packageHandle(selectedPackage);
    const preferredRoles = ALTERNATIVE_ROLE_ORDER[String(selectedPackage?.role || '')] || [];
    const candidates = rankPackages(packages, answers).filter((pkg) => packageHandle(pkg) !== selectedHandle);

    return candidates.sort((a, b) => {
      const aIndex = preferredRoles.indexOf(String(a.role || ''));
      const bIndex = preferredRoles.indexOf(String(b.role || ''));
      const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

      if (aRank !== bRank) return aRank - bRank;
      return scorePackage(b, answers) - scorePackage(a, answers);
    });
  }

  function buildResultText(pkg) {
    const baseText = pkg.resultText || 'Diese Auswahl passt zu deinen Antworten.';
    return baseText;
  }

  function buildMatchReasons(pkg, answers) {
    const useCases = toList(pkg?.useCaseFit);
    const durations = toList(pkg?.durationFit);

    return [
      useCases.includes(answers.use_case) ? MATCH_REASON_LABELS.use_case?.[answers.use_case] : '',
      durations.includes(answers.duration) ? MATCH_REASON_LABELS.duration?.[answers.duration] : '',
      answers.caffeine === 'no_caffeine' && pkg?.caffeinePolicy === 'no_caffeine'
        ? MATCH_REASON_LABELS.caffeine.no_caffeine
        : '',
      answers.format !== 'open' && matchesFormat(pkg, answers.format)
        ? MATCH_REASON_LABELS.format?.[answers.format]
        : '',
    ]
      .filter(Boolean)
      .slice(0, 4);
  }

  function matchesFormat(pkg, selectedFormat) {
    const answer = String(selectedFormat || '').trim().toLowerCase();
    const formats = toList(pkg.formatFit).map((entry) => String(entry).trim().toLowerCase());

    if (!answer || answer === 'open') return true;
    if (!formats.length) return false;

    if (answer === 'drink') return formats.includes('drink');
    if (answer === 'gel') return formats.includes('gel');
    if (answer === 'solid') return formats.includes('solid') || formats.includes('bar') || formats.includes('chew');
    if (answer === 'mix') return formats.includes('mix');

    return false;
  }

  function eventPayload(config, answers, pkg, extra) {
    return {
      finder_version: config.finderVersion || 'finder-unknown',
      question_count: Number(config.questionCount || 4),
      answers_summary: { ...answers },
      primary_package_handle: packageHandle(pkg),
      primary_package_role: pkg ? pkg.role : '',
      result_type: extra && extra.result_type ? extra.result_type : 'standard',
      finder_source: config.finderSource || '',
      prefilled: Boolean(config.finderPrefillActive),
      page_location: window.location.href,
      ...extra,
    };
  }

  function pushAnalytics(root, name, detail) {
    const eventName = `${EVENT_PREFIX}${name}`;
    const payload = {
      event: eventName,
      finder_id: root.id || root.dataset.finderVersion || 'fuelbox-product-finder',
      ...detail,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    root.dispatchEvent(new CustomEvent(`fb:${eventName}`, { bubbles: true, detail: payload }));
  }

  function cleanParamValue(value, maxLength) {
    return String(value || '')
      .trim()
      .slice(0, maxLength);
  }

  function normalizeAnswerParam(value) {
    return cleanParamValue(value, 80).toLowerCase();
  }

  function resolveLegacyFormat(source, priority) {
    const normalizedSource = cleanParamValue(source, 120).toLowerCase();
    const normalizedPriority = normalizeAnswerParam(priority);
    if (!normalizedSource || !normalizedPriority) return '';
    return LEGACY_FORMAT_BY_SOURCE_PRIORITY[`${normalizedSource}|${normalizedPriority}`] || '';
  }

  function buildValidAnswerMap(answersButtons) {
    const validAnswers = {};

    answersButtons.forEach((button) => {
      const question = button.dataset.question;
      const answer = button.dataset.answer;
      if (!question || !answer) return;

      validAnswers[question] = validAnswers[question] || new Set();
      validAnswers[question].add(answer);
    });

    return validAnswers;
  }

  function readUrlPrefill(validAnswers, searchString) {
    if (typeof URLSearchParams === 'undefined') return null;

    const searchParams = new URLSearchParams(
      searchString === undefined ? window.location.search || '' : searchString
    );
    const answers = {};
    const ignored = [];
    let hasAnswerParam = false;
    const source = cleanParamValue(searchParams.get(PREFILL_SOURCE_PARAM), 120);

    if (!searchParams.has('fb_format') && searchParams.has(LEGACY_PRIORITY_PARAM)) {
      hasAnswerParam = true;
      const rawPriority = cleanParamValue(searchParams.get(LEGACY_PRIORITY_PARAM), 80);
      const legacyFormat = resolveLegacyFormat(source, rawPriority);
      const validFormats = validAnswers.format;

      if (legacyFormat && validFormats && validFormats.has(legacyFormat)) {
        answers.format = legacyFormat;
      } else {
        ignored.push({
          param: LEGACY_PRIORITY_PARAM,
          question: 'format',
          value: rawPriority,
          reason: legacyFormat ? 'invalid_answer' : 'unmapped_legacy_priority',
        });
      }
    }

    PREFILL_PARAMS.forEach(({ query, question }) => {
      if (!searchParams.has(query)) return;

      hasAnswerParam = true;
      const rawValue = cleanParamValue(searchParams.get(query), 80);
      const answer = normalizeAnswerParam(rawValue);
      const answersForQuestion = validAnswers[question];

      if (!answer) {
        ignored.push({ param: query, question, reason: 'empty' });
        return;
      }

      if (answersForQuestion && answersForQuestion.has(answer)) {
        answers[question] = answer;
        return;
      }

      ignored.push({ param: query, question, value: rawValue, reason: 'invalid_answer' });
    });

    if (!hasAnswerParam) return null;

    return {
      answers,
      ignored,
      source,
    };
  }

  function initFinder(root) {
    if (root.dataset.fbFinderInitialized === 'true') return;

    const config = parseConfig(root);
    if (!config) return;

    root.dataset.fbFinderInitialized = 'true';

    const steps = Array.from(root.querySelectorAll(SELECTORS.step));
    const stepsWrap = root.querySelector(SELECTORS.stepsWrap);
    const actions = root.querySelector(SELECTORS.actions);
    const answersButtons = Array.from(root.querySelectorAll(SELECTORS.answer));
    const progress = root.querySelector(SELECTORS.progress);
    const progressLabel = root.querySelector(SELECTORS.progressLabel);
    const result = root.querySelector(SELECTORS.result);
    const resultImage = root.querySelector(SELECTORS.resultImage);
    const resultTitle = root.querySelector(SELECTORS.resultTitle);
    const resultText = root.querySelector(SELECTORS.resultText);
    const resultReasons = root.querySelector(SELECTORS.resultReasons);
    const resultPrice = root.querySelector(SELECTORS.resultPrice);
    const resultBadges = root.querySelector(SELECTORS.resultBadges);
    const resultLink = root.querySelector(SELECTORS.resultLink);
    const addToCartButton = root.querySelector(SELECTORS.addToCart);
    const status = root.querySelector(SELECTORS.status);
    const alternatives = root.querySelector(SELECTORS.alternatives);
    const sticky = root.querySelector(SELECTORS.sticky);
    const stickyTitle = root.querySelector(SELECTORS.stickyTitle);
    const stickyAdd = root.querySelector(SELECTORS.stickyAdd);
    const backButton = root.querySelector(SELECTORS.back);
    const restartButton = root.querySelector(SELECTORS.restart);
    const validAnswers = buildValidAnswerMap(answersButtons);
    const requiredQuestions = steps.map((step) => questionForStep(step)).filter(Boolean);

    const state = {
      answers: {},
      currentStep: 0,
      selectedPackage: null,
      resultType: 'standard',
      quickAddFailed: false,
      isAdding: false,
      hasStarted: false,
    };

    function setAddBusy(isBusy) {
      const value = isBusy ? 'true' : 'false';
      root.setAttribute('aria-busy', value);
      if (addToCartButton) addToCartButton.setAttribute('aria-busy', value);
      if (stickyAdd) stickyAdd.setAttribute('aria-busy', value);
    }

    function choosePackage() {
      return choosePackageForAnswers(config.packages, state.answers);
    }

    function syncStepHeight() {
      if (!stepsWrap || !steps.length) return;

      const wrapWidth = stepsWrap.getBoundingClientRect().width;
      if (!wrapWidth) return;

      let maxHeight = 0;

      steps.forEach((step) => {
        const wasHidden = step.hidden;
        const originalPosition = step.style.position;
        const originalVisibility = step.style.visibility;
        const originalPointerEvents = step.style.pointerEvents;
        const originalInset = step.style.inset;
        const originalWidth = step.style.width;
        const originalZIndex = step.style.zIndex;

        step.hidden = false;
        step.style.position = 'absolute';
        step.style.visibility = 'hidden';
        step.style.pointerEvents = 'none';
        step.style.inset = '0 auto auto 0';
        step.style.width = `${wrapWidth}px`;
        step.style.zIndex = '-1';

        maxHeight = Math.max(maxHeight, step.getBoundingClientRect().height);

        step.hidden = wasHidden;
        step.style.position = originalPosition;
        step.style.visibility = originalVisibility;
        step.style.pointerEvents = originalPointerEvents;
        step.style.inset = originalInset;
        step.style.width = originalWidth;
        step.style.zIndex = originalZIndex;
      });

      if (maxHeight) {
        stepsWrap.style.setProperty('--fb-finder-steps-min-height', `${Math.ceil(maxHeight)}px`);
      }
    }

    function scheduleStepHeightSync() {
      window.requestAnimationFrame(syncStepHeight);
    }

    function updateProgress() {
      const count = Number(config.questionCount || steps.length || 4);
      const isResultVisible = Boolean(result && !result.hidden);
      const displayStep = isResultVisible ? count : Math.min(state.currentStep + 1, count);
      const label = isResultVisible ? 'Empfehlung bereit' : `Frage ${displayStep} von ${count}`;
      const percentage = isResultVisible || count <= 0 ? 100 : (displayStep / count) * 100;

      if (progressLabel) progressLabel.textContent = label;
      if (progress) {
        progress.style.setProperty('--fb-finder-progress', `${percentage}%`);
        progress.setAttribute('aria-valuenow', String(displayStep));
        progress.setAttribute('aria-valuemax', String(count));
        progress.setAttribute('aria-label', label);
      }

      if (backButton) backButton.hidden = state.currentStep === 0 || !result?.hidden;
    }

    function focusStep(index) {
      const step = steps[index];
      if (!step) return;

      const target = step.querySelector('.fb-finder__question') || step;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    function scrollFinderIntoView() {
      const bodyStyles = window.getComputedStyle(document.body);
      const headerHeight = Number.parseFloat(bodyStyles.getPropertyValue('--header-group-height')) || 0;
      const rootTop = root.getBoundingClientRect().top;
      const targetTop = window.scrollY + rootTop - headerHeight - 16;
      const isCoveredByHeader = rootTop < headerHeight + 12;
      const isFarBelowViewportStart = rootTop > window.innerHeight * 0.36;

      if (!isCoveredByHeader && !isFarBelowViewportStart) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    function questionForStep(step) {
      return step?.querySelector(SELECTORS.answer)?.dataset.question || '';
    }

    function hasAllRequiredAnswers() {
      return requiredQuestions.every((question) => Boolean(state.answers[question]));
    }

    function missingRequiredQuestions() {
      return requiredQuestions.filter((question) => !state.answers[question]);
    }

    function firstUnansweredStepIndex(startIndex = 0) {
      const minimumIndex = Math.max(0, Number(startIndex) || 0);

      return steps.findIndex((step, stepIndex) => {
        const question = questionForStep(step);
        return stepIndex >= minimumIndex && question && !state.answers[question];
      });
    }

    function showNextUnansweredStep(startIndex = 0) {
      const nextUnansweredIndex = firstUnansweredStepIndex(startIndex);

      if (nextUnansweredIndex !== -1) {
        showStep(nextUnansweredIndex);
        return;
      }

      if (hasAllRequiredAnswers()) {
        renderResult();
        return;
      }

      showStep(Math.min(Math.max(0, Number(startIndex) || 0), steps.length - 1));
    }

    function showStep(index) {
      state.currentStep = Math.max(0, Math.min(index, steps.length - 1));
      if (stepsWrap) stepsWrap.hidden = false;
      if (actions) actions.hidden = false;
      root.classList.remove('is-result-visible');
      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== state.currentStep;
        step.setAttribute('aria-hidden', stepIndex === state.currentStep ? 'false' : 'true');
      });
      if (result) result.hidden = true;
      if (sticky) sticky.hidden = true;
      scheduleStepHeightSync();
      updateProgress();
      focusStep(state.currentStep);
    }

    function setSelectedButton(question, answer) {
      answersButtons
        .filter((button) => button.dataset.question === question)
        .forEach((button) => {
          const isSelected = button.dataset.answer === answer;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
        });
    }

    function renderBadges(pkg) {
      if (!resultBadges) return;

      resultBadges.innerHTML = '';
      toList(pkg.reasonPills).slice(0, 4).forEach((pill) => {
        const badge = document.createElement('span');
        badge.className = 'fb-finder__badge';
        badge.textContent = pill;
        resultBadges.appendChild(badge);
      });
    }

    function renderMatchReasons(pkg) {
      if (!resultReasons) return;

      const reasons = buildMatchReasons(pkg, state.answers);
      resultReasons.innerHTML = '';
      resultReasons.hidden = reasons.length === 0;

      reasons.forEach((reason) => {
        const item = document.createElement('li');
        item.textContent = reason;
        resultReasons.appendChild(item);
      });
    }

    function hasComparePrice(pkg) {
      return Boolean(pkg && pkg.price && pkg.compareAtPrice && pkg.compareAtPrice !== pkg.price);
    }

    function renderPrice(pkg) {
      if (!resultPrice) return;

      resultPrice.innerHTML = '';
      resultPrice.hidden = !pkg?.price;
      if (!pkg?.price) return;

      if (hasComparePrice(pkg)) {
        const compare = document.createElement('s');
        compare.className = 'fb-finder__price-compare';
        compare.textContent = pkg.compareAtPrice;

        const sale = document.createElement('span');
        sale.className = 'fb-finder__price-sale';
        sale.textContent = pkg.price;

        resultPrice.append(compare, sale);

        if (pkg.savings) {
          const saving = document.createElement('span');
          saving.className = 'fb-finder__saving';
          saving.textContent = pkg.savingsPercent
            ? `Du sparst ${pkg.savings} (${pkg.savingsPercent}%)`
            : `Du sparst ${pkg.savings}`;
          resultPrice.appendChild(saving);
        }

        return;
      }

      const regular = document.createElement('span');
      regular.className = 'fb-finder__price-sale';
      regular.textContent = pkg.price;
      resultPrice.appendChild(regular);
    }

    function renderAlternatives(selectedPackage, rankAnswers) {
      if (!alternatives) return;

      const alternativesList = rankAlternatives(config.packages, selectedPackage, rankAnswers || state.answers).slice(0, 2);

      alternatives.innerHTML = '';
      alternatives.hidden = alternativesList.length === 0;

      if (!alternativesList.length) return;

      const heading = document.createElement('p');
      heading.className = 'fb-finder__alternatives-heading';
      heading.textContent = 'Auch passend';
      alternatives.appendChild(heading);

      const list = document.createElement('div');
      list.className = 'fb-finder__alternatives-list';

      alternativesList.forEach((pkg) => {
        const item = document.createElement('a');
        item.className = 'fb-finder__alternative';
        item.href = pkg.url || config.routes.fallback || '/collections/testpakete';
        item.setAttribute('aria-label', `${pkg.resultTitle || pkg.productTitle || 'Alternative Box'} ansehen`);

        const media = document.createElement('span');
        media.className = 'fb-finder__alternative-media';

        if (pkg.image) {
          const image = document.createElement('img');
          image.src = pkg.image;
          image.alt = '';
          image.loading = 'lazy';
          media.appendChild(image);
        } else {
          const mark = document.createElement('span');
          mark.className = 'fb-finder__alternative-mark';
          mark.textContent = 'Box';
          media.appendChild(mark);
        }

        const body = document.createElement('span');
        body.className = 'fb-finder__alternative-body';

        const title = document.createElement('strong');
        title.textContent = pkg.resultTitle || pkg.productTitle || 'Alternative Box';

        const meta = document.createElement('span');
        meta.className = 'fb-finder__alternative-meta';
        const pill = toList(pkg.reasonPills)[0];
        const priceLabel = hasComparePrice(pkg) ? `${pkg.price} statt ${pkg.compareAtPrice}` : pkg.price;
        meta.textContent = [priceLabel, pill].filter(Boolean).join(' · ');

        const action = document.createElement('span');
        action.className = 'fb-finder__alternative-action';
        action.textContent = 'Details';

        body.append(title, meta, action);
        item.append(media, body);
        list.appendChild(item);
      });

      alternatives.appendChild(list);
    }

    function renderEmptyResult() {
      state.selectedPackage = null;
      state.resultType = 'empty';

      steps.forEach((step) => {
        step.hidden = true;
        step.setAttribute('aria-hidden', 'true');
      });
      if (stepsWrap) stepsWrap.hidden = true;
      if (actions) actions.hidden = true;
      root.classList.add('is-result-visible');

      if (result) result.hidden = false;
      if (resultImage) resultImage.closest('.fb-finder__result-media')?.setAttribute('hidden', '');
      if (resultTitle) {
        resultTitle.textContent = 'Testpakete ansehen';
        resultTitle.setAttribute('tabindex', '-1');
      }
      if (resultText) {
        resultText.textContent =
          state.answers.caffeine === 'no_caffeine'
            ? 'Für diese Kombination haben wir aktuell keine fertige Box, die sicher ohne Koffein ist. In der Übersicht findest du weitere Testpakete.'
            : 'Wir haben gerade keine Box gefunden, die alle Antworten sauber abdeckt. In der Testpakete-Übersicht findest du den nächsten sinnvollen Einstieg.';
      }
      if (resultReasons) {
        resultReasons.innerHTML = '';
        resultReasons.hidden = true;
      }
      if (resultPrice) {
        resultPrice.innerHTML = '';
        resultPrice.hidden = true;
      }
      if (resultBadges) resultBadges.innerHTML = '';
      if (resultLink) {
        resultLink.href = config.routes.fallback || '/collections/testpakete';
        resultLink.textContent = 'Testpakete ansehen';
      }
      if (addToCartButton) addToCartButton.hidden = true;
      if (sticky) sticky.hidden = true;
      if (alternatives) alternatives.hidden = true;
      if (status) status.textContent = 'Wir zeigen dir den besten verfügbaren Einstieg.';
      if (backButton) backButton.hidden = true;

      updateProgress();
      window.requestAnimationFrame(scrollFinderIntoView);
      resultTitle?.focus?.({ preventScroll: true });
      pushAnalytics(root, 'fallback_used', eventPayload(config, state.answers, null, { result_type: 'empty' }));
    }

    function renderResult() {
      const choice = choosePackage();
      const pkg = choice.pkg;

      if (!pkg) {
        renderEmptyResult();
        return;
      }

      state.selectedPackage = pkg;
      state.resultType = choice.resultType;
      state.quickAddFailed = false;

      steps.forEach((step) => {
        step.hidden = true;
        step.setAttribute('aria-hidden', 'true');
      });
      if (stepsWrap) stepsWrap.hidden = true;
      if (actions) actions.hidden = true;
      root.classList.add('is-result-visible');

      if (result) result.hidden = false;
      if (backButton) backButton.hidden = true;

      if (resultTitle) {
        resultTitle.textContent = pkg.resultTitle || pkg.productTitle || 'FuelBox Testpaket';
        resultTitle.setAttribute('tabindex', '-1');
      }
      if (resultText) {
        const text = buildResultText(pkg, state.answers);
        resultText.textContent =
          choice.resultType === 'fallback' ? `${buildFallbackNote(state.answers)} ${text}` : text;
      }
      renderPrice(pkg);
      if (resultLink) {
        resultLink.href = pkg.url || config.routes.fallback || '/collections/testpakete';
        resultLink.textContent = 'Details ansehen';
      }

      const imageWrap = resultImage?.closest('.fb-finder__result-media');
      if (resultImage && pkg.image) {
        resultImage.src = pkg.image;
        resultImage.alt = pkg.resultTitle || pkg.productTitle || 'FuelBox Testpaket';
        imageWrap?.removeAttribute('hidden');
      } else {
        resultImage?.removeAttribute('src');
        imageWrap?.setAttribute('hidden', '');
      }

      if (addToCartButton) {
        addToCartButton.hidden = false;
        addToCartButton.disabled = !isPurchasable(pkg);
        addToCartButton.textContent = isPurchasable(pkg) ? 'In den Warenkorb' : 'Details ansehen';
      }

      if (sticky) sticky.hidden = !isPurchasable(pkg);
      if (stickyTitle) stickyTitle.textContent = pkg.resultTitle || pkg.productTitle || 'Testpaket';
      if (stickyAdd) {
        stickyAdd.textContent = 'In den Warenkorb';
        stickyAdd.disabled = !isPurchasable(pkg);
      }

      renderBadges(pkg);
      renderMatchReasons(pkg);
      renderAlternatives(pkg, choice.rankAnswers);

      if (status) status.textContent = '';

      updateProgress();
      window.requestAnimationFrame(scrollFinderIntoView);
      resultTitle?.focus?.({ preventScroll: true });
      pushAnalytics(root, 'completed', eventPayload(config, state.answers, pkg, { result_type: choice.resultType }));
      pushAnalytics(root, 'result_view', eventPayload(config, state.answers, pkg, { result_type: choice.resultType }));
      if (choice.resultType === 'fallback') {
        pushAnalytics(root, 'fallback_used', eventPayload(config, state.answers, pkg, { result_type: 'fallback' }));
      }
    }

    function applyUrlPrefill() {
      const prefill = readUrlPrefill(validAnswers);
      if (!prefill) return;

      config.finderSource = prefill.source || 'url_prefill';
      config.finderPrefillActive = true;

      Object.entries(prefill.answers).forEach(([question, answer]) => {
        state.answers[question] = answer;
        setSelectedButton(question, answer);
      });

      const missingQuestions = missingRequiredQuestions();
      const firstMissingStepIndex = firstUnansweredStepIndex(0);
      const firstMissingQuestion = firstMissingStepIndex === -1 ? '' : questionForStep(steps[firstMissingStepIndex]);
      const appliedAnswerCount = Object.keys(prefill.answers).length;
      const prefillComplete = hasAllRequiredAnswers();
      const prefillChoice = prefillComplete ? choosePackage() : { pkg: null, resultType: 'partial' };

      pushAnalytics(
        root,
        'prefill_applied',
        eventPayload(config, state.answers, prefillChoice.pkg, {
          result_type: prefillComplete ? 'prefill_complete' : 'prefill_partial',
          prefill_source: prefill.source,
          prefill_answers: { ...prefill.answers },
          prefill_answer_count: appliedAnswerCount,
          prefill_ignored_params: prefill.ignored,
          prefill_missing_questions: missingQuestions,
          prefill_complete: prefillComplete,
          prefill_result_type: prefillChoice.resultType,
          first_unanswered_question: firstMissingQuestion,
          first_unanswered_step_index: firstMissingStepIndex,
        })
      );

      if (prefillComplete) {
        renderResult();
        return;
      }

      showStep(firstMissingStepIndex === -1 ? 0 : firstMissingStepIndex);
    }

    function answerQuestion(button) {
      const question = button.dataset.question;
      const answer = button.dataset.answer;
      const next = button.dataset.next;

      if (!question || !answer) return;

      if (!state.hasStarted) {
        pushAnalytics(root, 'start', eventPayload(config, state.answers, null, { result_type: 'start' }));
        state.hasStarted = true;
      }

      state.answers[question] = answer;
      setSelectedButton(question, answer);
      pushAnalytics(
        root,
        'question_answered',
        eventPayload(config, state.answers, state.selectedPackage, {
          question_id: question,
          answer_id: answer,
          step_index: state.currentStep,
        })
      );

      if (hasAllRequiredAnswers()) {
        renderResult();
        return;
      }

      if (next === RESULT_STEP) {
        showNextUnansweredStep(0);
        return;
      }

      showNextUnansweredStep(Number(next || state.currentStep + 1));
    }

    function collectCartSections() {
      return Array.from(document.querySelectorAll('cart-items-component'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
    }

    async function addSelectedPackage(trigger) {
      if (state.isAdding) return;

      const pkg = state.selectedPackage;
      if (state.quickAddFailed && pkg && pkg.url) {
        window.location.href = pkg.url;
        return;
      }
      if (!isPurchasable(pkg)) {
        if (pkg && pkg.url) window.location.href = pkg.url;
        return;
      }

      const button = trigger || addToCartButton;
      const originalText = button ? button.textContent : '';
      const sections = collectCartSections();
      const endpoint = config.routes.cartAdd || window.Theme?.routes?.cart_add_url || '/cart/add.js';
      let didAdd = false;

      state.isAdding = true;
      if (addToCartButton) addToCartButton.disabled = true;
      if (stickyAdd) stickyAdd.disabled = true;
      setAddBusy(true);
      if (button) {
        button.textContent = 'Wird hinzugefügt...';
      }
      if (status) status.textContent = '';

      pushAnalytics(root, 'add_to_cart_attempt', eventPayload(config, state.answers, pkg, { variant_id: pkg.variantId }));

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number(pkg.variantId), quantity: 1 }],
            sections: sections.join(','),
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Cart add failed');
        }

        didAdd = true;
        if (status) status.textContent = 'Im Warenkorb.';
        if (addToCartButton) {
          addToCartButton.textContent = 'Im Warenkorb';
          addToCartButton.disabled = true;
        }
        if (stickyAdd) {
          stickyAdd.textContent = 'Im Warenkorb';
          stickyAdd.disabled = true;
          stickyAdd.hidden = true;
        }
        if (sticky) sticky.hidden = true;
        if (resultLink) {
          resultLink.href = config.routes.cart || '/cart';
          resultLink.textContent = 'Zum Warenkorb';
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: root.id || 'fuelbox-product-finder',
              data: {
                source: 'fuelbox-product-finder',
                itemCount: 1,
                variantId: String(pkg.variantId),
                sections: data.sections || {},
              },
            },
          })
        );

        pushAnalytics(root, 'add_to_cart_success', eventPayload(config, state.answers, pkg, { variant_id: pkg.variantId }));
      } catch (error) {
        console.warn('[FuelBox Finder] Add to cart failed', error);
        if (status) status.textContent = 'Dieses Paket konnte gerade nicht direkt hinzugefügt werden. Öffne die Produktseite und füge es dort hinzu.';
        if (button) button.textContent = 'Details ansehen';
        if (addToCartButton) {
          addToCartButton.textContent = 'Details ansehen';
          addToCartButton.disabled = false;
        }
        if (stickyAdd) {
          stickyAdd.disabled = false;
          stickyAdd.hidden = true;
        }
        if (sticky) sticky.hidden = true;
        state.quickAddFailed = true;
        pushAnalytics(
          root,
          'add_to_cart_error',
          eventPayload(config, state.answers, pkg, {
            variant_id: pkg.variantId,
            error_message: error && error.message ? error.message : 'unknown',
          })
        );
      } finally {
        state.isAdding = false;
        setAddBusy(false);
        if (!didAdd && button && button.textContent !== 'Details ansehen') {
          window.setTimeout(() => {
            if (addToCartButton) addToCartButton.disabled = false;
            if (stickyAdd) stickyAdd.disabled = false;
            button.textContent = originalText || 'Testpaket in den Warenkorb';
          }, 1400);
        }
      }
    }

    answersButtons.forEach((button) => {
      button.addEventListener('click', () => answerQuestion(button));
    });

    backButton?.addEventListener('click', () => showStep(state.currentStep - 1));
    restartButton?.addEventListener('click', () => {
      state.answers = {};
      state.selectedPackage = null;
      state.resultType = 'standard';
      state.quickAddFailed = false;
      state.isAdding = false;
      state.hasStarted = false;
      setAddBusy(false);
      answersButtons.forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed', 'false');
      });
      if (addToCartButton) addToCartButton.hidden = false;
      showStep(0);
    });
    addToCartButton?.addEventListener('click', () => addSelectedPackage(addToCartButton));
    stickyAdd?.addEventListener('click', () => addSelectedPackage(stickyAdd));
    resultLink?.addEventListener('click', () => {
      pushAnalytics(
        root,
        'recommendation_click',
        eventPayload(config, state.answers, state.selectedPackage, { result_type: state.resultType })
      );
    });

    syncStepHeight();
    if (document.fonts?.ready) document.fonts.ready.then(syncStepHeight).catch(() => {});
    window.addEventListener('resize', scheduleStepHeightSync, { passive: true });
    updateProgress();
    pushAnalytics(root, 'view', eventPayload(config, state.answers, null, { result_type: 'view' }));
    applyUrlPrefill();
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initFinder);
  }

  if (window.__FB_FINDER_TEST__) {
    window.__FB_FINDER_TEST_API__ = {
      buildFallbackNote,
      buildMatchReasons,
      choosePackageForAnswers,
      eligiblePackageList,
      matchesFormat,
      preferredRolesForAnswers,
      rankAlternatives,
      rankPackages,
      readUrlPrefill,
      resolveLegacyFormat,
      scorePackage,
      violatesHardRules,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
